import React, { useEffect, useMemo } from 'react';
import { Box, FormGroup, Grid, Stack, Typography } from '@mui/material';
import { GoBackOnSkills, GoToNextStep, StickyPanel } from '../../../components';
import { useScrollToTop } from '../../../hooks';
import { useAppDispatch, useAppSelector } from '../../../redux';
import { skillsSelectors } from '../../../redux/skills';
import { DEVELOPER_TYPES } from '../../../constants';
import { gamifiedResponseSlice } from '../../../redux/gamifiedResponse';
import { getPrimarySkillsBySpecialization } from '../../../utils/techSkills';
import { useTechSkillsContext } from '../TechSkills';
import { PrimarySkillCheckbox } from './PrimarySkillCheckbox';
import { NoneOfTheAboveCheckbox } from './NoneOfTheAboveCheckbox';
import { StyledGridContainer } from './StyledGridContainer';
import { userSelectors } from 'src/redux/user';
import { logEvent } from 'src/services';

const PrimarySkills: React.FC = () => {
  useScrollToTop();
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(userSelectors.getIsUserPerformingAction);
  const skillsData = useAppSelector(skillsSelectors.getSkillsData);
  const userTechStack = useAppSelector(skillsSelectors.getTechStack);
  const {
    handlePrimarySkillsChange,
    handleNotOurCoreStackToggle,
    handleSubmitPrimarySkills,
    goBackToSpecialization,
    goBackToWorkArea,
  } = useTechSkillsContext();

  const primarySkills = getPrimarySkillsBySpecialization(
    userTechStack?.specialization || '',
    skillsData.data.skills,
  );

  useEffect(() => {
    if (userTechStack?.primarySkills?.length) {
      dispatch(gamifiedResponseSlice.actions.updateMessage(null));
    }

    logEvent('launchpod-primary-skills-page-loaded');
  }, []);

  const isSkillsGrid = (skills: any) => {
    return skills.length > 6;
  };

  const isContinueDisabled = () => {
    if (userTechStack?.notOurCoreStack) {
      return false;
    }
    return (
      !userTechStack?.primarySkills ||
      userTechStack?.primarySkills?.length === 0 ||
      userTechStack?.primarySkills?.length > 3 ||
      isMissingStackSide
    );
  };

  const isSelectedSkill = (skillName: string) => {
    return userTechStack?.primarySkills?.includes(skillName) ?? false;
  };

  const { backendSkills, frontendSkills, specializationSkills } =
    useMemo(() => {
      const filterSkillsBySpecialization = (
        type: (typeof DEVELOPER_TYPES)[keyof typeof DEVELOPER_TYPES],
      ) =>
        primarySkills.filter(
          (skillObject) => skillObject.specialization === type,
        );

      const isFullStack =
        userTechStack?.specialization === DEVELOPER_TYPES.fullstack;

      return {
        backendSkills: isFullStack
          ? filterSkillsBySpecialization(DEVELOPER_TYPES.backend)
          : [],
        frontendSkills: isFullStack
          ? filterSkillsBySpecialization(DEVELOPER_TYPES.frontend)
          : [],
        specializationSkills: primarySkills.filter(
          (skillObject) =>
            skillObject.specialization === userTechStack?.specialization,
        ),
      };
    }, [primarySkills, userTechStack]);

  // Full-stack candidates must pick at least one backend and one frontend technology
  const isMissingStackSide = useMemo(() => {
    if (userTechStack?.specialization !== DEVELOPER_TYPES.fullstack) {
      return false;
    }
    const selectedSkills = userTechStack?.primarySkills ?? [];
    const hasSelectedFrom = (skills: typeof primarySkills) =>
      skills.some((skillObject) => selectedSkills.includes(skillObject.name));

    return !hasSelectedFrom(backendSkills) || !hasSelectedFrom(frontendSkills);
  }, [backendSkills, frontendSkills, userTechStack]);

  return (
    <>
      <GoBackOnSkills
        workArea={userTechStack?.workArea || ''}
        goBackToWorkArea={goBackToWorkArea}
        specialization={userTechStack?.specialization}
        goBackToSpecialization={goBackToSpecialization}
      />
      <Typography variant="h2">
        Technologies you want to work with and{' '}
        <Box
          component="br"
          sx={(theme) => ({
            [theme.breakpoints.down('xl')]: {
              display: 'none',
            },
          })}
        />
        <strong>know well enough to complete a coding test</strong>
      </Typography>
      <Typography variant="body1" marginBottom="2rem">
        We have open positions with these technologies:
      </Typography>
      <FormGroup>
        {userTechStack?.specialization === DEVELOPER_TYPES.fullstack ? (
          <>
            <Stack direction={{ xs: 'column', xl: 'row' }} spacing={4}>
              <Box maxWidth={{ md: '100%', xl: '45vw' }} width={'100%'}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Backend
                </Typography>
                <StyledGridContainer
                  itemsLength={backendSkills.length}
                  columns={{
                    xs: 1,
                    sm: 2,
                    md: 2,
                    lg: 3,
                    xl: 2,
                  }}
                >
                  {backendSkills.map((skillObject) => (
                    <Box key={skillObject.name}>
                      <PrimarySkillCheckbox
                        value={skillObject.name}
                        onClick={handlePrimarySkillsChange}
                        isSelected={isSelectedSkill(skillObject.name)}
                      />
                    </Box>
                  ))}
                </StyledGridContainer>
              </Box>
              <Box maxWidth={{ md: '100%', xl: '15vw' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Frontend
                </Typography>
                <Grid container rowSpacing={0.5} columnSpacing={2}>
                  {frontendSkills.map((skillObject) => (
                    <Grid
                      item
                      key={skillObject.name}
                      xs={12}
                      sm={6}
                      md={6}
                      lg={4}
                      xl={12}
                    >
                      <PrimarySkillCheckbox
                        value={skillObject.name}
                        onClick={handlePrimarySkillsChange}
                        isSelected={isSelectedSkill(skillObject.name)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Stack>
            <Box maxWidth={{ xl: '100%' }} mt={6}>
              <NoneOfTheAboveCheckbox
                value={userTechStack?.notOurCoreStack}
                isSelected={userTechStack?.notOurCoreStack}
                onClick={handleNotOurCoreStackToggle}
              />
            </Box>
          </>
        ) : (
          <>
            {isSkillsGrid(primarySkills) ? (
              <>
                <Box maxWidth={{ md: '100%', xl: '45vw' }}>
                  <StyledGridContainer
                    itemsLength={specializationSkills.length}
                    columns={{
                      xs: 1,
                      sm: 2,
                      md: 2,
                      lg: 3,
                      xl: 3,
                    }}
                  >
                    {specializationSkills.map((skillObject) => (
                      <Box key={skillObject.name}>
                        <PrimarySkillCheckbox
                          value={skillObject.name}
                          onClick={handlePrimarySkillsChange}
                          isSelected={isSelectedSkill(skillObject.name)}
                        />
                      </Box>
                    ))}
                  </StyledGridContainer>
                </Box>
                <Box maxWidth={{ md: '100%', xl: '45vw' }} mt={6}>
                  <NoneOfTheAboveCheckbox
                    value={userTechStack?.notOurCoreStack}
                    isSelected={userTechStack?.notOurCoreStack}
                    onClick={handleNotOurCoreStackToggle}
                  />
                </Box>
              </>
            ) : (
              <Stack direction="column" spacing={1} maxWidth={{ sm: '17rem' }}>
                {specializationSkills.map((skillObject) => (
                  <PrimarySkillCheckbox
                    key={skillObject.name}
                    value={skillObject.name}
                    onClick={handlePrimarySkillsChange}
                    isSelected={isSelectedSkill(skillObject.name)}
                  />
                ))}
                <NoneOfTheAboveCheckbox
                  value={userTechStack?.notOurCoreStack}
                  isSelected={userTechStack?.notOurCoreStack}
                  onClick={handleNotOurCoreStackToggle}
                />
              </Stack>
            )}
          </>
        )}
      </FormGroup>
      <StickyPanel delayCalculation={400}>
        <GoToNextStep
          isLoading={isLoading}
          isDisabled={isContinueDisabled()}
          textIdentifier={1}
          handleSubmitStep={handleSubmitPrimarySkills}
          validationError={
            userTechStack?.primarySkills &&
            userTechStack?.primarySkills?.length > 3
              ? 'Please select up to 3'
              : userTechStack?.primarySkills?.length && isMissingStackSide
              ? 'Please select at least one backend and one frontend technology'
              : undefined
          }
          maxWidth={{ xs: '10rem', sm: 'initial' }}
        />
      </StickyPanel>
    </>
  );
};

export default PrimarySkills;
